import React from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Box,
  Button,
  Rating,
  Chip,
  Divider,
  IconButton,
  Avatar,
  Stack,
  useTheme,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import AddShoppingCart from '@mui/icons-material/AddShoppingCart';
import LocalShipping from '@mui/icons-material/LocalShipping';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import Store from '@mui/icons-material/Store';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import type { Product } from '../../services/types';

interface ProductQuickViewProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  onAddToCart: (product: Product) => void;
}

const ProductQuickView: React.FC<ProductQuickViewProps> = ({
  open,
  onClose,
  product,
  onAddToCart,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' },
      }}
    >
      <Box sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Grid container>
          {/* Product Image */}
          <Grid item xs={12} md={6} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'grey.100' }}>
            <Box
              component="img"
              src={product.image}
              alt={product.name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                minHeight: 400,
              }}
            />
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Chip label={product.category} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {product.name}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Rating value={product.rating} readOnly precision={0.5} size="small" />
                  <Typography variant="body2" color="text.secondary">
                    ({product.reviews} reviews)
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    KSh {product.price.toLocaleString()}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      KSh {product.originalPrice.toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Typography variant="body1" paragraph color="text.secondary">
                {product.description}
              </Typography>

              {/* Key Features */}
              <Box sx={{ mb: 3 }}>
                {product.features.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Seller Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'grey.50', borderRadius: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <Store />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {product.seller.name}
                    </Typography>
                    {product.seller.verified && (
                      <VerifiedUser sx={{ fontSize: 16, color: 'primary.main' }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {product.seller.location} • {product.seller.rating} ★
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onClose();
                    navigate(`/marketplace/seller/${product.seller.id}`);
                  }}
                >
                  Visit Store
                </Button>
              </Box>

              <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  startIcon={<Visibility />}
                  onClick={() => {
                    onClose();
                    navigate(`/marketplace/product/${product.id}`);
                  }}
                  sx={{ py: 1.5, fontWeight: 'bold' }}
                >
                  View Full Details
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<AddShoppingCart />}
                  onClick={() => {
                    onAddToCart(product);
                    onClose();
                  }}
                  sx={{ py: 1.5, fontWeight: 'bold' }}
                >
                  Add to Cart
                </Button>
                <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1, color: 'text.secondary' }}>
                  <LocalShipping sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  Standard delivery available
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ProductQuickView;
